/**
 * TRIATHLON LOOP — ARTICLE ENGINE
 * Version 5.0 — 2026-08-16
 *
 * Framework-free, dependency-free, progressively enhanced.
 * Works with Triathlon Loop Article Design System v5.0.
 *
 * Responsibilities:
 * - Legacy step-number repair
 * - data-* → CSS visual values
 * - reveal/counter motion
 * - accessible tabs and segmented controls
 * - contextual calculators
 * - SVG line/area charts
 * - grouped bar charts
 * - donut charts
 * - sortable/filterable tables
 * - FAQ single-open mode
 * - scrollytelling activation
 * - before/after comparison slider
 * - reading progress
 *
 * Article content remains useful when JS is unavailable.
 */

(function () {
  "use strict";

  var ROOT_SELECTOR = ".tl-article";
  var SVG_NS = "http://www.w3.org/2000/svg";
  var chartSequence = 0;

  var prefersReducedMotion =
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  var calculatorRegistry = Object.create(null);

  /* ------------------------------------------------------------------------
     CALCULATOR REGISTRY
     Inputs are read from data-tl-input="a", "b", "c", etc.
     ------------------------------------------------------------------------ */

  calculatorRegistry.add = function (v) {
    return v.a + v.b;
  };

  calculatorRegistry.subtract = function (v) {
    return v.a - v.b;
  };

  calculatorRegistry.multiply = function (v) {
    return v.a * v.b;
  };

  calculatorRegistry.divide = function (v) {
    return v.b === 0 ? NaN : v.a / v.b;
  };

  calculatorRegistry.percent = function (v) {
    return v.b === 0 ? NaN : (v.a / v.b) * 100;
  };

  calculatorRegistry.wkg = function (v) {
    return v.b === 0 ? NaN : v.a / v.b;
  };

  calculatorRegistry.if = function (v) {
    return v.b === 0 ? NaN : v.a / v.b;
  };

  calculatorRegistry.tss = function (v) {
    return v.a * Math.pow(v.b, 2) * 100;
  };

  calculatorRegistry["carbs-total"] = function (v) {
    return v.a * v.b;
  };

  calculatorRegistry["hydration-total"] = function (v) {
    return v.a * v.b;
  };

  calculatorRegistry["speed-kmh"] = function (v) {
    return v.b === 0 ? NaN : v.a / v.b;
  };

  calculatorRegistry["bike-time-hours"] = function (v) {
    return v.b === 0 ? NaN : v.a / v.b;
  };

  calculatorRegistry["run-finish-seconds"] = function (v) {
    return v.a * v.b;
  };

  calculatorRegistry["ftp-percent"] = function (v) {
    return v.a * (v.b / 100);
  };

  /* ------------------------------------------------------------------------
     HELPERS
     ------------------------------------------------------------------------ */

  function qsa(selector, scope) {
    return Array.prototype.slice.call(
      (scope || document).querySelectorAll(selector)
    );
  }

  function qs(selector, scope) {
    return (scope || document).querySelector(selector);
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function toNumber(value) {
    if (typeof value === "number") return value;

    var cleaned = String(value == null ? "" : value)
      .trim()
      .replace(/\s/g, "")
      .replace(",", ".")
      .replace(/[^\d.+-]/g, "");

    var n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : NaN;
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function formatNumber(value, decimals) {
    if (!Number.isFinite(value)) return "—";

    return value.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  }

  function svgEl(name, attrs) {
    var el = document.createElementNS(SVG_NS, name);

    Object.keys(attrs || {}).forEach(function (key) {
      el.setAttribute(key, String(attrs[key]));
    });

    return el;
  }

  function dispatch(el, name, detail) {
    if (!el || typeof CustomEvent !== "function") return;

    el.dispatchEvent(
      new CustomEvent(name, {
        bubbles: true,
        detail: detail || {}
      })
    );
  }

  function parseCellValue(cell) {
    if (!cell) return "";

    var explicit = cell.getAttribute("data-value");

    if (explicit != null && explicit !== "") {
      var explicitNumber = toNumber(explicit);
      return Number.isFinite(explicitNumber) ? explicitNumber : explicit;
    }

    var text = cell.textContent.trim();
    var numeric = toNumber(text);

    return Number.isFinite(numeric) && /[\d]/.test(text)
      ? numeric
      : text.toLocaleLowerCase();
  }

  /* ------------------------------------------------------------------------
     LEGACY STEP REPAIR
     Fixes the v4.x bug where a literal number and a CSS-generated number could
     appear in the same bubble.
     ------------------------------------------------------------------------ */

  function initStepNumbers(root) {
    qsa(".tl-steps", root).forEach(function (steps) {
      var items = qsa(":scope > .tl-step", steps);

      items.forEach(function (step, index) {
        var explicitNumber = step.querySelector(":scope > .tl-step__number");

        if (explicitNumber) {
          step.setAttribute("data-tl-has-step-number", "true");

          if (!explicitNumber.textContent.trim()) {
            explicitNumber.textContent = String(index + 1);
          }
        } else {
          step.removeAttribute("data-tl-has-step-number");
        }
      });
    });
  }

  /* ------------------------------------------------------------------------
     CSS DATA VARIABLES
     ------------------------------------------------------------------------ */

  function setDataVariables(root) {
    qsa("[data-tl-value]", root).forEach(function (el) {
      var value = clamp(toNumber(el.getAttribute("data-tl-value")), 0, 100);

      if (Number.isFinite(value)) {
        el.style.setProperty("--tl-value", value + "%");
      }
    });

    qsa("[data-tl-start], [data-tl-end], [data-tl-marker]", root).forEach(
      function (el) {
        var start = toNumber(el.getAttribute("data-tl-start"));
        var end = toNumber(el.getAttribute("data-tl-end"));
        var marker = toNumber(el.getAttribute("data-tl-marker"));

        if (Number.isFinite(start)) {
          el.style.setProperty("--tl-start", clamp(start, 0, 100) + "%");
        }

        if (Number.isFinite(end)) {
          el.style.setProperty("--tl-end", clamp(end, 0, 100) + "%");
        }

        if (Number.isFinite(marker)) {
          el.style.setProperty("--tl-marker", clamp(marker, 0, 100) + "%");
        }
      }
    );
  }

  /* ------------------------------------------------------------------------
     REVEAL MOTION
     ------------------------------------------------------------------------ */

  function initReveal(root) {
    var nodes = qsa("[data-tl-reveal], .tl-animate-in", root);

    if (!nodes.length) return;

    if (prefersReducedMotion || !("IntersectionObserver" in window)) {
      nodes.forEach(function (el) {
        el.classList.add("is-visible");
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;

          entry.target.classList.add("is-visible");
          obs.unobserve(entry.target);
        });
      },
      {
        rootMargin: "0px 0px -8% 0px",
        threshold: 0.08
      }
    );

    nodes.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ------------------------------------------------------------------------
     COUNTERS
     ------------------------------------------------------------------------ */

  function initCounters(root) {
    qsa("[data-tl-count-to]", root).forEach(function (el) {
      if (el.getAttribute("data-tl-count-ready") === "true") return;

      var target = toNumber(el.getAttribute("data-tl-count-to"));
      if (!Number.isFinite(target)) return;

      var decimals = parseInt(el.getAttribute("data-decimals") || "0", 10);
      var prefix = el.getAttribute("data-prefix") || "";
      var suffix = el.getAttribute("data-suffix") || "";

      function setValue(value) {
        el.textContent = prefix + formatNumber(value, decimals) + suffix;
      }

      function run() {
        if (prefersReducedMotion) {
          setValue(target);
          el.setAttribute("data-tl-count-ready", "true");
          return;
        }

        var start = performance.now();
        var duration = 720;

        function tick(now) {
          var progress = clamp((now - start) / duration, 0, 1);
          var eased = 1 - Math.pow(1 - progress, 3);

          setValue(target * eased);

          if (progress < 1) {
            requestAnimationFrame(tick);
          } else {
            setValue(target);
            el.setAttribute("data-tl-count-ready", "true");
          }
        }

        requestAnimationFrame(tick);
      }

      if (!("IntersectionObserver" in window)) {
        run();
        return;
      }

      var observer = new IntersectionObserver(
        function (entries, obs) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            run();
            obs.unobserve(entry.target);
          });
        },
        { threshold: 0.35 }
      );

      observer.observe(el);
    });
  }

  /* ------------------------------------------------------------------------
     ACCESSIBLE TABS
     New markup: data-tl-tabs / data-tl-tab / data-tl-panel
     Old CSS-radio tabs continue working without this code.
     ------------------------------------------------------------------------ */

  function initTabs(root) {
    qsa("[data-tl-tabs]", root).forEach(function (tabs, tabsIndex) {
      if (tabs.getAttribute("data-tl-enhanced") === "tabs") return;

      var buttons = qsa("[data-tl-tab]", tabs);
      var panels = qsa("[data-tl-panel]", tabs);

      if (!buttons.length || !panels.length) return;

      var list = tabs.querySelector(".tl-tabs__list");

      if (list) {
        list.setAttribute("role", "tablist");
      }

      function activate(id, focus) {
        buttons.forEach(function (button) {
          var selected = button.getAttribute("data-tl-tab") === id;

          button.setAttribute("aria-selected", selected ? "true" : "false");
          button.setAttribute("tabindex", selected ? "0" : "-1");

          if (selected && focus) {
            button.focus();
          }
        });

        panels.forEach(function (panel) {
          var active = panel.getAttribute("data-tl-panel") === id;
          panel.hidden = !active;
        });

        dispatch(tabs, "tl:tabchange", { id: id });
      }

      buttons.forEach(function (button, index) {
        var id = button.getAttribute("data-tl-tab");
        var panel = panels.find(function (candidate) {
          return candidate.getAttribute("data-tl-panel") === id;
        });

        button.setAttribute("role", "tab");
        button.id = button.id || "tl-tab-" + tabsIndex + "-" + index;

        if (panel) {
          panel.setAttribute("role", "tabpanel");
          panel.id = panel.id || "tl-panel-" + tabsIndex + "-" + index;
          panel.setAttribute("aria-labelledby", button.id);
          button.setAttribute("aria-controls", panel.id);
        }

        button.addEventListener("click", function () {
          activate(id, false);
        });

        button.addEventListener("keydown", function (event) {
          var current = buttons.indexOf(button);
          var next = current;

          if (event.key === "ArrowRight" || event.key === "ArrowDown") {
            next = (current + 1) % buttons.length;
          } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
            next = (current - 1 + buttons.length) % buttons.length;
          } else if (event.key === "Home") {
            next = 0;
          } else if (event.key === "End") {
            next = buttons.length - 1;
          } else {
            return;
          }

          event.preventDefault();
          activate(buttons[next].getAttribute("data-tl-tab"), true);
        });
      });

      var initiallySelected =
        buttons.find(function (button) {
          return button.getAttribute("aria-selected") === "true";
        }) || buttons[0];

      activate(initiallySelected.getAttribute("data-tl-tab"), false);
      tabs.setAttribute("data-tl-enhanced", "tabs");
    });
  }

  /* ------------------------------------------------------------------------
     SEGMENTED CONTROLS
     ------------------------------------------------------------------------ */

  function initSegmented(root) {
    qsa("[data-tl-segmented]", root).forEach(function (control) {
      if (control.getAttribute("data-tl-enhanced") === "segmented") return;

      var buttons = qsa("button[data-value]", control);
      var targetSelector = control.getAttribute("data-target");
      var target = targetSelector ? document.querySelector(targetSelector) : null;

      buttons.forEach(function (button) {
        button.addEventListener("click", function () {
          buttons.forEach(function (candidate) {
            candidate.setAttribute(
              "aria-pressed",
              candidate === button ? "true" : "false"
            );
          });

          if (target) {
            target.value = button.getAttribute("data-value");
            target.dispatchEvent(new Event("input", { bubbles: true }));
            target.dispatchEvent(new Event("change", { bubbles: true }));
          }

          dispatch(control, "tl:segmentchange", {
            value: button.getAttribute("data-value")
          });
        });
      });

      if (
        buttons.length &&
        !buttons.some(function (button) {
          return button.getAttribute("aria-pressed") === "true";
        })
      ) {
        buttons[0].setAttribute("aria-pressed", "true");
      }

      control.setAttribute("data-tl-enhanced", "segmented");
    });
  }

  /* ------------------------------------------------------------------------
     CALCULATORS
     ------------------------------------------------------------------------ */

  function collectCalculatorValues(calc) {
    var values = Object.create(null);

    qsa("[data-tl-input]", calc).forEach(function (input) {
      values[input.getAttribute("data-tl-input")] = toNumber(input.value);
    });

    return values;
  }

  function formatCalculatorResult(calc, result) {
    var mode = calc.getAttribute("data-format") || "number";
    var decimals = parseInt(calc.getAttribute("data-decimals") || "2", 10);
    var prefix = calc.getAttribute("data-prefix") || "";
    var suffix = calc.getAttribute("data-suffix") || "";

    if (!Number.isFinite(result)) return null;

    if (mode === "duration") {
      var totalSeconds = Math.round(result);
      var hours = Math.floor(totalSeconds / 3600);
      var minutes = Math.floor((totalSeconds % 3600) / 60);
      var seconds = totalSeconds % 60;

      if (hours > 0) {
        return (
          hours +
          ":" +
          String(minutes).padStart(2, "0") +
          ":" +
          String(seconds).padStart(2, "0")
        );
      }

      return minutes + ":" + String(seconds).padStart(2, "0");
    }

    if (mode === "hours-duration") {
      var totalMinutes = Math.round(result * 60);
      var h = Math.floor(totalMinutes / 60);
      var m = totalMinutes % 60;

      return h + "h " + String(m).padStart(2, "0") + "m";
    }

    return prefix + formatNumber(result, decimals) + suffix;
  }

  function initCalculators(root) {
    qsa("[data-tl-calc]", root).forEach(function (calc) {
      if (calc.getAttribute("data-tl-enhanced") === "calculator") return;

      var name = calc.getAttribute("data-tl-calc");
      var fn = calculatorRegistry[name];
      var output = calc.querySelector("[data-tl-output]");
      var inputs = qsa("[data-tl-input]", calc);

      if (!fn || !output || !inputs.length) return;

      var invalidText =
        calc.getAttribute("data-invalid") || "Check the inputs";

      function update() {
        var values = collectCalculatorValues(calc);
        var validInputs = Object.keys(values).every(function (key) {
          return Number.isFinite(values[key]);
        });

        var result = validInputs ? fn(values, calc) : NaN;
        var formatted = formatCalculatorResult(calc, result);

        if (formatted != null) {
          output.textContent = formatted;
          output.removeAttribute("data-invalid");
        } else {
          output.textContent = invalidText;
          output.setAttribute("data-invalid", "true");
        }

        dispatch(calc, "tl:calculate", {
          calculator: name,
          values: values,
          result: result
        });
      }

      inputs.forEach(function (input) {
        input.addEventListener("input", update);
        input.addEventListener("change", update);
      });

      update();
      calc.setAttribute("data-tl-enhanced", "calculator");
    });
  }

  /* ------------------------------------------------------------------------
     RANGE OUTPUTS
     <input type=range data-tl-range="#output-id">
     ------------------------------------------------------------------------ */

  function initRangeOutputs(root) {
    qsa('input[type="range"][data-tl-range]', root).forEach(function (input) {
      if (input.getAttribute("data-tl-enhanced-range") === "true") return;

      var target = document.querySelector(input.getAttribute("data-tl-range"));
      if (!target) return;

      function update() {
        var prefix = input.getAttribute("data-prefix") || "";
        var suffix = input.getAttribute("data-suffix") || "";
        target.textContent = prefix + input.value + suffix;
      }

      input.addEventListener("input", update);
      update();
      input.setAttribute("data-tl-enhanced-range", "true");
    });
  }

  /* ------------------------------------------------------------------------
     CHART DATA
     Semantic HTML table remains the source of truth.
     ------------------------------------------------------------------------ */

  function readChartData(chart) {
    var table = chart.querySelector("table[data-tl-chart-data]");
    if (!table) return null;

    var headers = qsa("thead th", table).map(function (th) {
      return th.textContent.trim();
    });

    var rows = qsa("tbody tr", table);
    if (!rows.length) return null;

    var labels = [];
    var rawRows = [];

    rows.forEach(function (row) {
      var cells = qsa("th, td", row);
      if (cells.length < 2) return;

      labels.push(cells[0].textContent.trim());

      rawRows.push(
        cells.slice(1).map(function (cell) {
          var explicit = cell.getAttribute("data-value");
          return toNumber(explicit != null ? explicit : cell.textContent);
        })
      );
    });

    if (!rawRows.length) return null;

    var seriesCount = Math.min(
      3,
      Math.max.apply(
        null,
        rawRows.map(function (row) {
          return row.length;
        })
      )
    );

    var series = [];

    for (var s = 0; s < seriesCount; s++) {
      series.push({
        name: headers[s + 1] || "Series " + (s + 1),
        values: rawRows.map(function (row) {
          return row[s];
        })
      });
    }

    return {
      table: table,
      labels: labels,
      series: series
    };
  }

  function createLegend(chart, data) {
    var old = chart.querySelector(
      ".tl-viz__legend[data-generated='true'], .tl-chart-legend[data-generated='true']"
    );

    if (old) old.remove();

    if (
      data.series.length <= 1 &&
      !chart.hasAttribute("data-show-legend")
    ) {
      return;
    }

    var legend = document.createElement("div");
    legend.className = "tl-viz__legend";
    legend.setAttribute("data-generated", "true");

    data.series.forEach(function (series, index) {
      var item = document.createElement("span");
      item.className = "tl-viz__legend-item";

      var swatch = document.createElement("i");
      swatch.className =
        "tl-viz__legend-swatch tl-viz__legend-swatch--" + (index + 1);

      var label = document.createElement("span");
      label.textContent = series.name;

      item.appendChild(swatch);
      item.appendChild(label);
      legend.appendChild(item);
    });

    var toggle = chart.querySelector(".tl-data-table-toggle");

    if (toggle) {
      chart.insertBefore(legend, toggle);
    } else {
      chart.appendChild(legend);
    }
  }

  function renderLineChart(chart, data, areaMode) {
    var canvas = chart.querySelector("[data-tl-chart-canvas]");
    if (!canvas) return;

    canvas.innerHTML = "";

    chartSequence += 1;

    var width = 800;
    var height = 360;
    var margin = { top: 28, right: 24, bottom: 54, left: 66 };
    var plotW = width - margin.left - margin.right;
    var plotH = height - margin.top - margin.bottom;

    var allValues = [];

    data.series.forEach(function (series) {
      series.values.forEach(function (value) {
        if (Number.isFinite(value)) allValues.push(value);
      });
    });

    if (!allValues.length) {
      canvas.innerHTML =
        '<div class="tl-viz-empty">No numeric chart data found.</div>';
      return;
    }

    var explicitMin = toNumber(chart.getAttribute("data-tl-min"));
    var explicitMax = toNumber(chart.getAttribute("data-tl-max"));
    var dataMin = Math.min.apply(null, allValues);
    var dataMax = Math.max.apply(null, allValues);

    var yMin = Number.isFinite(explicitMin)
      ? explicitMin
      : Math.min(0, dataMin);

    var yMax = Number.isFinite(explicitMax)
      ? explicitMax
      : dataMax * 1.1;

    if (yMax === yMin) yMax = yMin + 1;

    var unit = chart.getAttribute("data-unit") || "";
    var svg = svgEl("svg", {
      viewBox: "0 0 " + width + " " + height,
      preserveAspectRatio: "xMidYMid meet",
      "aria-hidden": "true",
      focusable: "false"
    });

    var defs = svgEl("defs");

    var gradientId = "tl-area-gradient-" + chartSequence;
    var gradient = svgEl("linearGradient", {
      id: gradientId,
      x1: "0",
      y1: "0",
      x2: "0",
      y2: "1"
    });

    gradient.appendChild(
      svgEl("stop", {
        offset: "0%",
        "stop-color": "#a64937",
        "stop-opacity": ".34"
      })
    );

    gradient.appendChild(
      svgEl("stop", {
        offset: "100%",
        "stop-color": "#a64937",
        "stop-opacity": "0"
      })
    );

    defs.appendChild(gradient);
    svg.appendChild(defs);

    function xFor(index) {
      if (data.labels.length <= 1) {
        return margin.left + plotW / 2;
      }

      return margin.left + (index / (data.labels.length - 1)) * plotW;
    }

    function yFor(value) {
      return (
        margin.top +
        (1 - (value - yMin) / (yMax - yMin)) * plotH
      );
    }

    var gridCount = 5;

    for (var g = 0; g <= gridCount; g++) {
      var ratio = g / gridCount;
      var y = margin.top + ratio * plotH;
      var value = yMax - ratio * (yMax - yMin);

      svg.appendChild(
        svgEl("line", {
          x1: margin.left,
          x2: width - margin.right,
          y1: y,
          y2: y,
          class: "tl-chart-gridline"
        })
      );

      var yLabel = svgEl("text", {
        x: margin.left - 10,
        y: y + 4,
        "text-anchor": "end",
        class: "tl-chart-axis-label"
      });

      yLabel.textContent =
        formatNumber(value, Math.abs(value) >= 100 ? 0 : 1) + unit;

      svg.appendChild(yLabel);
    }

    var labelStep = Math.max(
      1,
      Math.ceil(data.labels.length / 7)
    );

    data.labels.forEach(function (labelText, index) {
      if (
        index % labelStep !== 0 &&
        index !== data.labels.length - 1
      ) {
        return;
      }

      var x = xFor(index);

      var xLabel = svgEl("text", {
        x: x,
        y: height - 18,
        "text-anchor": "middle",
        class: "tl-chart-axis-label"
      });

      xLabel.textContent = labelText;
      svg.appendChild(xLabel);
    });

    data.series.forEach(function (series, seriesIndex) {
      var points = [];

      series.values.forEach(function (value, index) {
        if (!Number.isFinite(value)) return;

        points.push({
          x: xFor(index),
          y: yFor(value),
          value: value,
          index: index
        });
      });

      if (!points.length) return;

      var pathD = points
        .map(function (point, index) {
          return (
            (index === 0 ? "M " : "L ") +
            point.x +
            " " +
            point.y
          );
        })
        .join(" ");

      if ((areaMode || seriesIndex === 0) && points.length > 1) {
        var areaD =
          pathD +
          " L " +
          points[points.length - 1].x +
          " " +
          (margin.top + plotH) +
          " L " +
          points[0].x +
          " " +
          (margin.top + plotH) +
          " Z";

        var area = svgEl("path", {
          d: areaD,
          fill: "url(#" + gradientId + ")"
        });

        area.setAttribute("opacity", areaMode ? ".55" : ".28");
        svg.appendChild(area);
      }

      svg.appendChild(
        svgEl("path", {
          d: pathD,
          class:
            "tl-chart-path tl-chart-path--" +
            (seriesIndex + 1)
        })
      );

      points.forEach(function (point) {
        svg.appendChild(
          svgEl("circle", {
            cx: point.x,
            cy: point.y,
            r: 5,
            class:
              "tl-chart-point tl-chart-point--" +
              (seriesIndex + 1)
          })
        );
      });
    });

    var crosshair = svgEl("line", {
      x1: margin.left,
      x2: margin.left,
      y1: margin.top,
      y2: margin.top + plotH,
      class: "tl-chart-crosshair"
    });

    crosshair.style.opacity = "0";
    svg.appendChild(crosshair);

    var hit = svgEl("rect", {
      x: margin.left,
      y: margin.top,
      width: plotW,
      height: plotH,
      fill: "transparent",
      style: "cursor: crosshair;"
    });

    svg.appendChild(hit);
    canvas.appendChild(svg);

    var tooltip = document.createElement("div");
    tooltip.className = "tl-chart-tooltip";
    tooltip.setAttribute("role", "status");
    tooltip.setAttribute("aria-live", "polite");
    canvas.appendChild(tooltip);

    function showAt(index) {
      index = clamp(index, 0, data.labels.length - 1);

      var x = xFor(index);

      crosshair.setAttribute("x1", x);
      crosshair.setAttribute("x2", x);
      crosshair.style.opacity = "1";

      var html =
        "<strong>" + escapeHtml(data.labels[index]) + "</strong>";

      data.series.forEach(function (series) {
        var value = series.values[index];
        if (!Number.isFinite(value)) return;

        var decimals = chart.hasAttribute("data-decimals")
          ? parseInt(chart.getAttribute("data-decimals"), 10)
          : Math.abs(value) >= 100
          ? 0
          : 1;

        html +=
          "<span>" +
          escapeHtml(series.name) +
          ": " +
          escapeHtml(formatNumber(value, decimals)) +
          escapeHtml(unit) +
          "</span>";
      });

      tooltip.innerHTML = html;
      tooltip.style.left = (x / width) * 100 + "%";
      tooltip.style.top = "48%";
      tooltip.classList.add("is-visible");
    }

    function hide() {
      tooltip.classList.remove("is-visible");
      crosshair.style.opacity = "0";
    }

    function resolveIndex(event) {
      var rect = svg.getBoundingClientRect();
      var svgX =
        ((event.clientX - rect.left) / rect.width) * width;

      var ratio = clamp(
        (svgX - margin.left) / plotW,
        0,
        1
      );

      return Math.round(ratio * (data.labels.length - 1));
    }

    hit.addEventListener("pointermove", function (event) {
      showAt(resolveIndex(event));
    });

    hit.addEventListener("pointerdown", function (event) {
      showAt(resolveIndex(event));
    });

    hit.addEventListener("pointerleave", hide);
  }

  function renderBarChart(chart, data) {
    var canvas = chart.querySelector("[data-tl-chart-canvas]");
    if (!canvas) return;

    canvas.innerHTML = "";

    var explicitMax = toNumber(chart.getAttribute("data-tl-max"));
    var allValues = [];

    data.series.forEach(function (series) {
      series.values.forEach(function (value) {
        if (Number.isFinite(value)) allValues.push(value);
      });
    });

    if (!allValues.length) {
      canvas.innerHTML =
        '<div class="tl-viz-empty">No numeric chart data found.</div>';
      return;
    }

    var max = Number.isFinite(explicitMax)
      ? explicitMax
      : Math.max.apply(null, allValues);

    if (max <= 0) max = 1;

    var unit = chart.getAttribute("data-unit") || "";
    var decimals = chart.hasAttribute("data-decimals")
      ? parseInt(chart.getAttribute("data-decimals"), 10)
      : 0;

    var wrapper = document.createElement("div");
    wrapper.className = "tl-chart-bars";

    data.labels.forEach(function (label, rowIndex) {
      var group = document.createElement("div");
      group.className = "tl-chart-bars__group";

      var groupLabel = document.createElement("span");
      groupLabel.className = "tl-chart-bars__label";
      groupLabel.textContent = label;
      group.appendChild(groupLabel);

      var seriesWrap = document.createElement("div");
      seriesWrap.className = "tl-chart-bars__series";

      data.series.forEach(function (series, seriesIndex) {
        var value = series.values[rowIndex];
        if (!Number.isFinite(value)) return;

        var row = document.createElement("div");
        row.className = "tl-chart-bars__row";

        var name = document.createElement("span");
        name.className = "tl-chart-bars__name";
        name.textContent = series.name;

        var track = document.createElement("div");
        track.className = "tl-chart-bars__track";

        var fill = document.createElement("div");
        fill.className =
          "tl-chart-bars__fill tl-chart-bars__fill--" +
          (seriesIndex + 1);

        fill.style.setProperty(
          "--tl-value",
          clamp((value / max) * 100, 0, 100) + "%"
        );

        track.appendChild(fill);

        var valueEl = document.createElement("span");
        valueEl.className = "tl-chart-bars__value";
        valueEl.textContent =
          formatNumber(value, decimals) + unit;

        row.appendChild(name);
        row.appendChild(track);
        row.appendChild(valueEl);
        seriesWrap.appendChild(row);
      });

      group.appendChild(seriesWrap);
      wrapper.appendChild(group);
    });

    canvas.appendChild(wrapper);
  }

  function renderDonutChart(chart, data) {
    var canvas = chart.querySelector("[data-tl-chart-canvas]");
    if (!canvas) return;

    canvas.innerHTML = "";

    var series = data.series[0];
    if (!series) return;

    var entries = data.labels
      .map(function (label, index) {
        return {
          label: label,
          value: series.values[index]
        };
      })
      .filter(function (entry) {
        return Number.isFinite(entry.value) && entry.value >= 0;
      });

    if (!entries.length) {
      canvas.innerHTML =
        '<div class="tl-viz-empty">No numeric chart data found.</div>';
      return;
    }

    var total = entries.reduce(function (sum, entry) {
      return sum + entry.value;
    }, 0);

    if (total <= 0) return;

    var wrapper = document.createElement("div");
    wrapper.className = "tl-donut-chart";

    var size = 260;
    var center = size / 2;
    var radius = 92;
    var stroke = 34;
    var circumference = 2 * Math.PI * radius;

    var svg = svgEl("svg", {
      viewBox: "0 0 " + size + " " + size,
      "aria-hidden": "true",
      focusable: "false"
    });

    svg.appendChild(
      svgEl("circle", {
        cx: center,
        cy: center,
        r: radius,
        fill: "none",
        stroke: "#e8edef",
        "stroke-width": stroke
      })
    );

    var palette = [
      "#a64937",
      "#18303e",
      "#3f7775",
      "#6b617f",
      "#a77635",
      "#6d846f"
    ];

    var offset = 0;

    entries.forEach(function (entry, index) {
      var portion = entry.value / total;
      var length = portion * circumference;

      var circle = svgEl("circle", {
        cx: center,
        cy: center,
        r: radius,
        fill: "none",
        stroke: palette[index % palette.length],
        "stroke-width": stroke,
        "stroke-dasharray": length + " " + (circumference - length),
        "stroke-dashoffset": -offset,
        transform: "rotate(-90 " + center + " " + center + ")"
      });

      svg.appendChild(circle);
      offset += length;
    });

    var centerValue = svgEl("text", {
      x: center,
      y: center - 2,
      "text-anchor": "middle",
      fill: "#18272f",
      "font-size": "28",
      "font-weight": "850",
      "font-family": "inherit"
    });

    centerValue.textContent = formatNumber(total, 0);
    svg.appendChild(centerValue);

    var centerLabel = svgEl("text", {
      x: center,
      y: center + 22,
      "text-anchor": "middle",
      fill: "#69787f",
      "font-size": "12",
      "font-weight": "650",
      "font-family": "inherit"
    });

    centerLabel.textContent =
      chart.getAttribute("data-center-label") || "Total";

    svg.appendChild(centerLabel);

    var legend = document.createElement("div");
    legend.className = "tl-donut-chart__legend";

    entries.forEach(function (entry, index) {
      var row = document.createElement("div");
      row.className = "tl-donut-chart__item";

      var swatch = document.createElement("i");
      swatch.className = "tl-donut-chart__swatch";
      swatch.style.background = palette[index % palette.length];

      var label = document.createElement("span");
      label.textContent = entry.label;

      var value = document.createElement("strong");
      value.className = "tl-donut-chart__value";
      value.textContent = formatNumber(entry.value, 0);

      row.appendChild(swatch);
      row.appendChild(label);
      row.appendChild(value);
      legend.appendChild(row);
    });

    wrapper.appendChild(svg);
    wrapper.appendChild(legend);
    canvas.appendChild(wrapper);
  }

  function initCharts(root) {
    qsa("[data-tl-chart]", root).forEach(function (chart) {
      if (chart.getAttribute("data-tl-enhanced") === "chart") return;

      var type = chart.getAttribute("data-tl-chart");
      var data = readChartData(chart);

      if (!data) return;

      if (type === "line") {
        renderLineChart(chart, data, false);
      } else if (type === "area") {
        renderLineChart(chart, data, true);
      } else if (type === "bars" || type === "bar") {
        renderBarChart(chart, data);
      } else if (type === "donut") {
        renderDonutChart(chart, data);
      } else {
        return;
      }

      if (type !== "donut") {
        createLegend(chart, data);
      }

      chart.setAttribute("data-tl-enhanced", "chart");
      dispatch(chart, "tl:chartrender", { type: type });
    });
  }

  /* ------------------------------------------------------------------------
     SORTABLE TABLES
     Add data-tl-sortable to <table> or a containing .tl-table-wrap.
     ------------------------------------------------------------------------ */

  function initSortableTables(root) {
    var candidates = qsa(
      "table[data-tl-sortable], [data-tl-sortable] table",
      root
    );

    var tables = candidates.filter(function (table, index, all) {
      return all.indexOf(table) === index;
    });

    tables.forEach(function (table) {
      if (table.getAttribute("data-tl-enhanced-sort") === "true") return;

      var headers = qsa("thead th", table);
      var tbody = table.tBodies && table.tBodies[0];

      if (!headers.length || !tbody) return;

      headers.forEach(function (th, columnIndex) {
        if (th.hasAttribute("data-tl-nosort")) return;

        var original = th.innerHTML;

        var button = document.createElement("button");
        button.type = "button";
        button.className = "tl-table-sort-button";
        button.innerHTML = original;

        th.innerHTML = "";
        th.appendChild(button);
        th.setAttribute("aria-sort", "none");

        button.addEventListener("click", function () {
          var current = th.getAttribute("aria-sort");
          var next =
            current === "ascending" ? "descending" : "ascending";

          headers.forEach(function (header) {
            header.setAttribute("aria-sort", "none");
          });

          th.setAttribute("aria-sort", next);

          var rows = qsa("tr", tbody).map(function (row, index) {
            return {
              row: row,
              index: index,
              value: parseCellValue(row.cells[columnIndex])
            };
          });

          rows.sort(function (a, b) {
            var result;

            if (
              typeof a.value === "number" &&
              typeof b.value === "number"
            ) {
              result = a.value - b.value;
            } else {
              result = String(a.value).localeCompare(
                String(b.value),
                undefined,
                { numeric: true, sensitivity: "base" }
              );
            }

            if (result === 0) {
              result = a.index - b.index;
            }

            return next === "ascending" ? result : -result;
          });

          rows.forEach(function (entry) {
            tbody.appendChild(entry.row);
          });

          dispatch(table, "tl:tablesort", {
            column: columnIndex,
            direction: next
          });
        });
      });

      table.setAttribute("data-tl-enhanced-sort", "true");
    });
  }

  /* ------------------------------------------------------------------------
     FILTERABLE TABLES
     Add data-tl-filterable to wrapper/table and include an input data-tl-filter.
     ------------------------------------------------------------------------ */

  function initFilterableTables(root) {
    qsa("[data-tl-filterable]", root).forEach(function (container) {
      if (container.getAttribute("data-tl-enhanced-filter") === "true") return;

      var table =
        container.matches("table")
          ? container
          : container.querySelector("table");

      if (!table || !table.tBodies || !table.tBodies[0]) return;

      var input =
        container.querySelector("[data-tl-filter]") ||
        (container.parentElement &&
          container.parentElement.querySelector("[data-tl-filter]"));

      if (!input) return;

      var rows = qsa("tr", table.tBodies[0]);

      function update() {
        var query = input.value
          .trim()
          .toLocaleLowerCase();

        rows.forEach(function (row) {
          var haystack = row.textContent.toLocaleLowerCase();
          row.hidden = query ? haystack.indexOf(query) === -1 : false;
        });

        dispatch(table, "tl:tablefilter", { query: query });
      }

      input.addEventListener("input", update);
      container.setAttribute("data-tl-enhanced-filter", "true");
    });
  }

  /* ------------------------------------------------------------------------
     FAQ SINGLE-OPEN MODE
     Add data-tl-single-open to .tl-faq.
     ------------------------------------------------------------------------ */

  function initFaq(root) {
    qsa(".tl-faq[data-tl-single-open]", root).forEach(function (faq) {
      if (faq.getAttribute("data-tl-enhanced-faq") === "true") return;

      qsa("details", faq).forEach(function (detail) {
        detail.addEventListener("toggle", function () {
          if (!detail.open) return;

          qsa("details", faq).forEach(function (other) {
            if (other !== detail) other.open = false;
          });
        });
      });

      faq.setAttribute("data-tl-enhanced-faq", "true");
    });
  }

  /* ------------------------------------------------------------------------
     SCROLLYTELLING
     ------------------------------------------------------------------------ */

  function initScrolly(root) {
    qsa(".tl-scrolly", root).forEach(function (scrolly) {
      if (scrolly.getAttribute("data-tl-enhanced-scrolly") === "true") return;

      var steps = qsa(".tl-scrolly__step", scrolly);

      if (!steps.length) return;

      if (!("IntersectionObserver" in window)) {
        steps[0].classList.add("is-active");
        return;
      }

      var observer = new IntersectionObserver(
        function (entries) {
          var visible = entries
            .filter(function (entry) {
              return entry.isIntersecting;
            })
            .sort(function (a, b) {
              return b.intersectionRatio - a.intersectionRatio;
            });

          if (!visible.length) return;

          steps.forEach(function (step) {
            step.classList.remove("is-active");
          });

          visible[0].target.classList.add("is-active");

          dispatch(scrolly, "tl:scrollychange", {
            step: steps.indexOf(visible[0].target)
          });
        },
        {
          rootMargin: "-25% 0px -45% 0px",
          threshold: [0.15, 0.35, 0.6]
        }
      );

      steps.forEach(function (step) {
        observer.observe(step);
      });

      scrolly.setAttribute("data-tl-enhanced-scrolly", "true");
    });
  }

  /* ------------------------------------------------------------------------
     BEFORE / AFTER SLIDER
     ------------------------------------------------------------------------ */

  function initBeforeAfter(root) {
    qsa(".tl-before-after", root).forEach(function (component) {
      if (component.getAttribute("data-tl-enhanced-before-after") === "true") return;

      var input = component.querySelector('input[type="range"]');
      if (!input) return;

      function update() {
        var min = toNumber(input.min || "0");
        var max = toNumber(input.max || "100");
        var value = toNumber(input.value);

        if (!Number.isFinite(min)) min = 0;
        if (!Number.isFinite(max)) max = 100;
        if (!Number.isFinite(value)) value = 50;

        var ratio =
          max === min
            ? 50
            : ((value - min) / (max - min)) * 100;

        component.style.setProperty(
          "--tl-split",
          clamp(ratio, 0, 100) + "%"
        );
      }

      input.addEventListener("input", update);
      update();
      component.setAttribute("data-tl-enhanced-before-after", "true");
    });
  }

  /* ------------------------------------------------------------------------
     READING PROGRESS
     Uses the article's own scroll extent.
     ------------------------------------------------------------------------ */

  function initReadingProgress(root) {
    var progress = root.querySelector(".tl-reading-progress");
    if (!progress || progress.getAttribute("data-tl-enhanced-progress") === "true") return;

    var ticking = false;

    function update() {
      ticking = false;

      var rect = root.getBoundingClientRect();
      var viewport = window.innerHeight || document.documentElement.clientHeight;
      var total = Math.max(root.offsetHeight - viewport * 0.35, 1);
      var passed = clamp(-rect.top + viewport * 0.18, 0, total);
      var ratio = clamp(passed / total, 0, 1);

      progress.style.setProperty("--tl-reading-progress", ratio);
    }

    function requestUpdate() {
      if (ticking) return;

      ticking = true;
      requestAnimationFrame(update);
    }

    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate, { passive: true });
    progress.setAttribute("data-tl-enhanced-progress", "true");
    update();
  }

  /* ------------------------------------------------------------------------
     INITIALISATION
     ------------------------------------------------------------------------ */

  function initRoot(root) {
    initStepNumbers(root);
    setDataVariables(root);
    initReveal(root);
    initCounters(root);
    initTabs(root);
    initSegmented(root);
    initCalculators(root);
    initRangeOutputs(root);
    initCharts(root);
    initSortableTables(root);
    initFilterableTables(root);
    initFaq(root);
    initScrolly(root);
    initBeforeAfter(root);
    initReadingProgress(root);
  }

  function init(scope) {
    var roots = [];

    if (scope && scope.matches && scope.matches(ROOT_SELECTOR)) {
      roots = [scope];
    } else {
      roots = qsa(ROOT_SELECTOR, scope || document);
    }

    if (!roots.length) return;

    document.documentElement.classList.add("tl-js");

    roots.forEach(initRoot);
  }

  function registerCalculator(name, fn) {
    if (!name || typeof fn !== "function") return;

    calculatorRegistry[name] = fn;
  }

  function refresh(scope) {
    init(scope || document);
  }

  window.TLArticleEngine = {
    version: "5.0.0",
    init: init,
    refresh: refresh,
    registerCalculator: registerCalculator
  };

  /* Backward-compatible namespace used by v4.9. */
  window.TLArticleWidgets = window.TLArticleEngine;

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      function () {
        init(document);
      },
      { once: true }
    );
  } else {
    init(document);
  }
})();
